import React, { useEffect, useState, useRef } from "react";
import cx from "classnames";
import styles from "../scss/AFQuestionForm.module.scss";
import { IAnswerProps, IAppFormQuestion } from "../interfaces/IAppFormData";
import AppFormField from "./AppFormField";
import { Form, Formik } from "formik";
import {
    checkInValidAnswer,
    generateAnswerList,
    getQuestionHeaderText,
    showHelpText
} from "../shared/AppFormHelper";
import { ControlType } from "../enums/ControlType";
import Tooltip from "../../common/components/Tooltips/Tooltip";
import AFNavigation from "./AFNavigation";

interface IAFMultiFormFieldProps {
    applicantName: string;
    list: IAppFormQuestion[];
    formQuestionLabel: string;
    nextQuestion: (any) => void;
    fieldReady: (status: boolean) => void;
    handleBack: (e) => void;
    skipIsVisible: boolean;
    handleSkip?: () => void;
    showSkipDialog?: boolean;
}

const AFMultiFormField: React.FC<IAFMultiFormFieldProps> = (props) => {
    const { list, formQuestionLabel, nextQuestion, fieldReady, applicantName, handleBack, skipIsVisible, handleSkip, showSkipDialog } = props;

    const [questionListReady, setQuestionListReady] = useState(false);
    const [questionList, setQuestionList] = useState<IAppFormQuestion[]>([]);
    const [formReadyToSubmit, setFormReadyToSubmit] = useState(false);
    const [formAnswerList, setFormAnswerList] = useState<IAnswerProps[]>([]);
    const [lastestQuestion, setLastestQuestion] = useState<IAppFormQuestion>();
    const [searchTimeout, setSearchTimeout] = useState<any>(null);
    const [formQuestionHeaderText, setFormQuestionHeaderText] = useState("");
    const bottomRef = useRef(null);
    const formRef = useRef(null);

    const checkForms = (list): boolean => {
        const isReady = questionList.reduce((acc, item) => {
            const answerValue = list.find((i) => i.questionId === item.id);
            if (checkInValidAnswer(answerValue?.answer)) {
                acc = false;
            }
            return acc;
        }, true);
        setFormReadyToSubmit(isReady);
        return isReady;
    };

    const getQuestionNumber = (questionId: number): string => {
        return list.find((i) => i.id === questionId)?.questionNumber || "";
    };

    const handleAnswerChanges = (answerParam: IAnswerProps): void => {
        const answerList = questionList.reduce((acc, item) => {
            const currentAnswer = formAnswerList.find((i) => i.questionId === item.id);
            const answer =
                answerParam.questionId === item.id
                    ? answerParam.answer
                    : currentAnswer?.answer;
            const disclosure =
                answerParam.questionId === item.id
                    ? answerParam.disclosure
                    : currentAnswer?.disclosure;
            return [
                ...acc,
                {
                    questionId: item.id,
                    disclosure,
                    answer,
                    questionNumber: getQuestionNumber(item.id)
                }
            ];
        }, []);
        setFormAnswerList(answerList);
        checkForms(answerList);
        if (!checkForms(answerList)) return;
        if (
            lastestQuestion?.id === answerParam?.questionId &&
            lastestQuestion?.controlTypeId === ControlType.RADIOBUTTON
        ) {
            if (formRef.current) {
                const timeout = setTimeout(() => {
                    formRef.current?.handleSubmit(answerList);
                }, 500);

                setSearchTimeout(timeout);
            }
        }
    };

    const handleInitialQuestionList = (): void => {
        if (questionListReady) return;
        setFormQuestionHeaderText(getQuestionHeaderText(list[0]));
        const activeCount = list.filter((i) => i.answer)?.length || 0;
        let newList = questionList;
        if (activeCount) {
            newList = list;
        } else {
            const listCount = questionList?.length;
            newList.push(list[listCount]);
            setLastestQuestion(list[listCount]);
        }
        setQuestionList(newList);
        checkForms(generateAnswerList(newList));
        setFormAnswerList(generateAnswerList(newList));
        setQuestionListReady(true);
    };

    const handleSubmit = (answerList: IAnswerProps[] = []): void => {
        if (!formReadyToSubmit && !answerList.length) return;
        setQuestionListReady(false);
        const currentListCount = questionList?.length;
        if (list.length === currentListCount) {
            const answers = answerList?.length ? answerList : formAnswerList;
            nextQuestion(answers);
            return;
        }
        setFormReadyToSubmit(false);
        let newList = questionList;
        setLastestQuestion(list[currentListCount]);
        newList.push(list[currentListCount]);
        setQuestionList(newList);
        setQuestionListReady(true);
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        handleInitialQuestionList();
        return () => {
            clearTimeout(searchTimeout);
        };
    }, [fieldReady]);

    return (
        <div
            className={cx(styles["fa-question"])}>        
            <div className={styles["form-title"]}>
                <div
                    className={styles["form-title-client"]}
                    dangerouslySetInnerHTML={{
                        __html: applicantName
                    }}></div>
            </div>
            {formQuestionLabel && (
                <div
                    className={cx(
                        styles["form-title"],
                        formQuestionHeaderText &&
                            formQuestionLabel !== formQuestionHeaderText
                            ? styles["has-subtitle"]
                            : ""
                    )}>
                    <div
                        className={styles["form-title-label"]}
                        dangerouslySetInnerHTML={{
                            __html: formQuestionLabel || ""
                        }}></div>
                </div>
            )}

            {formQuestionHeaderText && formQuestionLabel !== formQuestionHeaderText && (
                <label
                    className={styles["form-title-label"]}
                    dangerouslySetInnerHTML={{
                        __html: formQuestionHeaderText || ""
                    }}></label>
            )}

            <Formik
                innerRef={formRef}
                initialValues={{}}
                onSubmit={() => {
                    handleSubmit();
                }}>
                {() => (
                    <Form autoComplete="off" className={styles["fa-question-form"]}>
                        {questionList?.map((question, idx) => (
                            <div
                                className={cx(
                                    styles["question-form"],
                                    "layout-flex-column--centered"
                                )}
                                key={idx}
                                id={`${question.id}-${idx}`}>
                                <label
                                    className={styles["question-form-label"]}
                                    htmlFor={
                                        question?.questionLabel || question?.questionText
                                    }
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            question?.questionLabel ||
                                            question?.questionText ||
                                            ""
                                    }}></label>
                                {!!showHelpText(question) && (
                                    <Tooltip
                                        className={styles["form-title-tooltip"]}
                                        tooltipContent={<div dangerouslySetInnerHTML={{ __html: showHelpText(question) }} />}
                                    />
                                )}

                                <div className={styles["question-form-field"]}>
                                    <AppFormField
                                        question={question}
                                        answerChanges={handleAnswerChanges}
                                        fieldReady={fieldReady}
                                        labelText={
                                            question?.questionLabel ||
                                            question?.questionText
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                        <AFNavigation
                            handleBack={handleBack}
                            skipIsVisible={skipIsVisible}
                            showSkipDialog={showSkipDialog}
                            handleSkip={handleSkip}
                            forwardIsVisible={true}
                            forwardIsDisabled={!formReadyToSubmit}
                            forwardType="submit"
                            forwardText={"Next"}
                        />
                        <div className={styles["fa-question-bottom"]} ref={bottomRef} />
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AFMultiFormField;
